import { useState, useEffect } from 'react';
import { Key, Loader2, Save, Trash2, Eye, EyeOff, Lock } from 'lucide-react';
import {
  setSessionPassphrase,
  storeApiKey,
  loadApiKey,
  deleteApiKey,
  clearSessionPassphrase,
} from '@/services/storage/secure';
import { useSettingsStore } from '@/stores/settings';

const PROVIDERS = [
  { id: 'aivis' as const, label: 'Aivis Cloud', desc: '音声合成(TTS)' },
  { id: 'claude' as const, label: 'Claude (Anthropic)', desc: 'シーン生成・スクリーニング' },
  { id: 'kie' as const, label: 'kie.ai Seedance', desc: 'フック動画生成' },
];

export function Settings() {
  const [passphrase, setPassphrase] = useState('');
  const [passphraseReady, setPassphraseReady] = useState(false);
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const { hasAivisKey, hasClaudeKey, hasKieKey, setHasKey, setPassphraseSet } =
    useSettingsStore();

  useEffect(() => {
    if (!passphraseReady) return;
    void (async () => {
      const next: Record<string, string> = {};
      for (const p of PROVIDERS) {
        const k = await loadApiKey(p.id);
        if (k) next[p.id] = k;
      }
      setKeys(next);
    })();
  }, [passphraseReady]);

  function unlockPassphrase() {
    if (!passphrase) return;
    setSessionPassphrase(passphrase);
    setPassphraseReady(true);
    setPassphraseSet(true);
  }

  async function handleSave(provider: 'aivis' | 'claude' | 'kie', key: string) {
    setSaving(provider);
    try {
      await storeApiKey(provider, key);
      setHasKey(provider, true);
      setKeys((prev) => ({ ...prev, [provider]: key }));
    } finally {
      setSaving(null);
    }
  }

  async function handleDelete(provider: 'aivis' | 'claude' | 'kie') {
    if (!confirm(`${provider} のキーを削除しますか?`)) return;
    await deleteApiKey(provider);
    setHasKey(provider, false);
    setKeys((prev) => {
      const copy = { ...prev };
      delete copy[provider];
      return copy;
    });
  }

  if (!passphraseReady) {
    return (
      <div className="px-4 py-6 max-w-md mx-auto">
        <h1 className="text-xl font-semibold mb-1">設定</h1>
        <p className="text-sm text-slate-400 mb-6">
          APIキー暗号化用のパスフレーズを入力してください。このパスフレーズはサーバーには送信されません。
        </p>
        <div className="card">
          <label className="label">マスターパスフレーズ</label>
          <input
            type="password"
            autoComplete="current-password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && unlockPassphrase()}
            className="input mb-3"
            placeholder="長めのパスフレーズを推奨"
          />
          <button onClick={unlockPassphrase} className="btn-primary w-full">
            <Key className="h-4 w-4" />
            解錠
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">設定 / APIキー</h1>
          <p className="text-xs text-slate-400">BYOK: 自分のAPIキーを暗号化保存</p>
        </div>
        <button
          onClick={() => {
            clearSessionPassphrase();
            setPassphraseReady(false);
            setPassphrase('');
            setKeys({});
          }}
          className="flex items-center gap-1 text-xs text-slate-400 px-2 py-1 rounded-lg active:bg-slate-800"
        >
          <Lock className="h-3 w-3" />
          施錠
        </button>
      </div>

      <div className="space-y-3">
        {PROVIDERS.map((p) => (
          <ProviderCard
            key={p.id}
            id={p.id}
            label={p.label}
            desc={p.desc}
            currentKey={keys[p.id] ?? ''}
            saving={saving === p.id}
            onSave={(k) => handleSave(p.id, k)}
            onDelete={() => handleDelete(p.id)}
            hasKey={
              p.id === 'aivis' ? hasAivisKey : p.id === 'claude' ? hasClaudeKey : hasKieKey
            }
          />
        ))}
      </div>
    </div>
  );
}

function ProviderCard({
  id,
  label,
  desc,
  currentKey,
  saving,
  hasKey,
  onSave,
  onDelete,
}: {
  id: string;
  label: string;
  desc: string;
  currentKey: string;
  saving: boolean;
  hasKey: boolean;
  onSave: (key: string) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(currentKey);
  const [visible, setVisible] = useState(false);
  useEffect(() => setDraft(currentKey), [currentKey]);

  return (
    <div className="card">
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0">
          <h3 className="font-medium text-sm">{label}</h3>
          <p className="text-xs text-slate-500 truncate">{desc}</p>
        </div>
        {hasKey && (
          <span className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
            保存済
          </span>
        )}
      </div>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="input pr-10"
          placeholder={`${id} API Key`}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-1 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center text-slate-400 active:bg-slate-800 rounded-lg"
          aria-label={visible ? 'キーを隠す' : 'キーを表示'}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={() => onSave(draft)}
          disabled={saving || !draft}
          className="btn-primary flex-1"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          保存
        </button>
        {hasKey && (
          <button onClick={onDelete} className="btn-ghost text-red-400 px-3">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
